"use client";

import { InferenceStatus, InferenceType, SegmentResult } from "@/lib/playground-utils";
import { Capability } from "./prompt-types";
import { Card, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import CapabilityPill from "./capability-pill";
import CodeExample from "./code-example";
import InferenceDetect from "./inference-detect";
import InferencePointing from "./inference-point";
import InferenceSegmentation from "./inference-segmentation";
import InferenceSegmentationMulti from "./inference-segmentation-multi";
import { captionLengthFromString } from "./prompt-caption";
import { useEffect, useRef, useState } from "react";
type PointData = { x: number; y: number }[];
type BoundingBoxData = {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
}[];

function renderCaptionResult(
  result: string | PointData | BoundingBoxData,
  status?: InferenceStatus,
) {
  if (status === "pending") {
    return (
      <span className="text-muted-foreground italic">Waiting…</span>
    );
  }

  if (status === "error") {
    const message =
      typeof result === "string" && result.length > 0
        ? result
        : "Unable to stream caption. Please try again.";
    return <span className="text-destructive">{message}</span>;
  }

  if (typeof result === "string") {
    const text = result.trimStart();
    return text.length > 0 ? text : "…";
  }

  return JSON.stringify(result);
}

function GroundingVisualization({
  image,
  points
}: {
  image: string;
  points: Array<[number, number]>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;

      const MAX_DIMENSION = 400; // Slightly smaller for hover card
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);

      if (points.length > 0) {
        const pointColor = "rgb(40, 141, 255)";
        const transparentColor = "rgba(40, 141, 255, 0.3)";

        points.forEach((point) => {
          const x = point[0] * scaledWidth;
          const y = point[1] * scaledHeight;
          const outerRadius = 8; // Slightly smaller than pointing
          const middleRadius = 8;
          const innerRadius = 2;

          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
          ctx.strokeStyle = pointColor;
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = transparentColor;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
          ctx.fillStyle = pointColor;
          ctx.fill();
        });
      }
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, points, scaledDimensions.width, scaledDimensions.height]);

  return (
    <div className="p-2">
      <canvas
        ref={canvasRef}
        className="rounded-lg"
        style={{
          width: `${scaledDimensions.width}px`,
          height: `${scaledDimensions.height}px`,
        }}
      />
    </div>
  );
}

function renderReasoningText(
  reasoning: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  },
  image: string
) {
  if (!reasoning.grounding || reasoning.grounding.length === 0) {
    return reasoning.text;
  }

  // Sort grounding by start_idx to process in order
  const sortedGrounding = [...reasoning.grounding].sort((a, b) => a.start_idx - b.start_idx);

  let lastIndex = 0;
  const parts: React.ReactNode[] = [];

  sortedGrounding.forEach((ground, index) => {
    // Add text before this grounded span
    if (ground.start_idx > lastIndex) {
      parts.push(reasoning.text.slice(lastIndex, ground.start_idx));
    }

    // Add the grounded span with hover card
    parts.push(
      <HoverCard key={index}>
        <HoverCardTrigger asChild>
          <span className="border-b border-dotted border-primary cursor-pointer">
            {reasoning.text.slice(ground.start_idx, ground.end_idx)}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-auto p-0">
          <GroundingVisualization image={image} points={ground.points} />
        </HoverCardContent>
      </HoverCard>
    );

    lastIndex = ground.end_idx;
  });

  // Add remaining text after the last grounded span
  if (lastIndex < reasoning.text.length) {
    parts.push(reasoning.text.slice(lastIndex));
  }

  return <>{parts}</>;
}

function InferenceVisualQuery({
  image,
  result,
  query,
  reasoning,
  duration,
  imageFile,
}: {
  image: string;
  result: string | PointData | BoundingBoxData;
  query: string;
  reasoning?: {
    text: string;
    grounding: Array<{
      start_idx: number;
      end_idx: number;
      points: Array<[number, number]>;
    }>;
  };
  duration?: number;
  imageFile?: File;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;

      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.VisualQuery} />
              </div>
              <div>
                <div className="text-xs font-black text-secondary-foreground">
                  PROMPT
                </div>
                <div className="text-sm break-words">{query}</div>
              </div>
              {reasoning && (
                <div>
                  <div className="text-xs font-black text-secondary-foreground">
                    REASONING
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {renderReasoningText(reasoning, image)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs font-black text-secondary-foreground">
                  RESULT
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">
                  {typeof result === "string" ? result.trimStart() : JSON.stringify(result)}
                </div>
              </div>
              {duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.VisualQuery}
            prompt={query}
            imageFile={imageFile}
          />
        )}
      </CardContent>
    </Card>
  );
}

function InferenceCaption({
  image,
  result,
  captionLength,
  duration,
  imageFile,
  status,
}: {
  image: string;
  result: string | PointData | BoundingBoxData;
  captionLength: string;
  duration?: number;
  imageFile?: File;
  status?: InferenceStatus;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;

      const MAX_DIMENSION = 512;
      let scaledWidth = imgElement.width;
      let scaledHeight = imgElement.height;

      if (scaledWidth > MAX_DIMENSION || scaledHeight > MAX_DIMENSION) {
        if (scaledWidth >= scaledHeight) {
          const aspectRatio = scaledHeight / scaledWidth;
          scaledWidth = MAX_DIMENSION;
          scaledHeight = MAX_DIMENSION * aspectRatio;
        } else {
          const aspectRatio = scaledWidth / scaledHeight;
          scaledHeight = MAX_DIMENSION;
          scaledWidth = MAX_DIMENSION * aspectRatio;
        }
      }

      canvas.width = scaledWidth * 2;
      canvas.height = scaledHeight * 2;

      // Let Tailwind handle responsive sizing, just set aspect ratio
      canvas.style.aspectRatio = `${scaledWidth} / ${scaledHeight}`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      if (
        scaledDimensions.width !== scaledWidth ||
        scaledDimensions.height !== scaledHeight
      ) {
        setScaledDimensions({ width: scaledWidth, height: scaledHeight });
      }

      ctx.drawImage(imgElement, 0, 0, scaledWidth, scaledHeight);
    };

    imgElement.src = image;

    return () => {
      isMounted = false;
    };
  }, [image, scaledDimensions.width, scaledDimensions.height]);

  return (
    <Card>
      <CardContent className="mt-6">
        <div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-full sm:w-auto">
              <canvas
                ref={canvasRef}
                className="rounded-lg w-full max-w-full sm:w-auto sm:max-w-md h-auto"
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-grow">
              <div>
                <CapabilityPill capability={Capability.Caption} />
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  LENGTH: {captionLength.toUpperCase()}
                </div>
              </div>
              <div>
                <div className="text-xs font-black text-muted-foreground">
                  RESULT
                </div>
                <div className="text-sm text-secondary-foreground whitespace-pre-wrap break-words">
                  {renderCaptionResult(result, status)}
                </div>
              </div>
              {status === "done" && duration && (
                <div className="text-xs text-muted-foreground mt-2">
                  Response time: {duration.toFixed(0)}ms
                </div>
              )}
            </div>
          </div>
        </div>
        {imageFile && (
          <CodeExample
            capability={Capability.Caption}
            prompt=""
            imageFile={imageFile}
            options={{ length: captionLengthFromString(captionLength) }}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function Inference({
  image,
  result,
  capability,
  query,
  captionLength,
  reasoning,
  duration,
  imageFile,
  status,
  segmentResult,
  segmentResults,
}: InferenceType) {
  const segmentationHueRef = useRef<number>(Math.floor(Math.random() * 360));

  switch (capability) {
    case Capability.VisualQuery:
      return (
        <InferenceVisualQuery
          image={image}
          result={result}
          query={query}
          reasoning={reasoning}
          duration={duration}
          imageFile={imageFile}
        />
      );
    case Capability.Caption:
      return (
        <InferenceCaption
          image={image}
          result={result}
          captionLength={captionLength as string}
          duration={duration}
          imageFile={imageFile}
          status={status}
        />
      );
    case Capability.Pointing:
      return (
        <InferencePointing
          image={image}
          result={result as PointData}
          query={query}
          duration={duration}
          imageFile={imageFile}
        />
      );
    case Capability.ObjectDetection:
      return (
        <InferenceDetect
          image={image}
          result={result as BoundingBoxData}
          query={query}
          duration={duration}
          imageFile={imageFile}
        />
      );
    case Capability.Segmentation:
      return (
        <InferenceSegmentation
          image={image}
          result={
            typeof result === "string"
              ? result
              : JSON.stringify(result, null, 2)
          }
          query={query}
          duration={duration}
          status={status}
          segmentResult={segmentResult}
          neonHue={segmentationHueRef.current}
          segmentResults={segmentResults}
          imageFile={imageFile}
        />
      );
    case Capability.PointSegment:
      return (
        <InferenceSegmentationMulti
          image={image}
          result={
            typeof result === "string"
              ? result
              : JSON.stringify(result, null, 2)
          }
          query={query}
          duration={duration}
          status={status}
          segmentResult={segmentResult}
          segmentResults={segmentResults as SegmentResult[] | undefined}
          neonHue={segmentationHueRef.current}
          imageFile={imageFile}
        />
      );
    default:
      throw new Error(`Unknown capability: ${capability}`);
  }
}
